
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface RouterLocation {
  readonly pathname: string;
  readonly search: string;
}

interface RouterContextValue {
  readonly location: RouterLocation;
  readonly navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

function readLocation(): RouterLocation {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '' };
  }
  return { pathname: window.location.pathname || '/', search: window.location.search };
}

function interceptableUrl(event: MouseEvent, anchor: HTMLAnchorElement): URL | null {
  if (event.defaultPrevented) return null;
  if (event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

  if (anchor.target && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download')) return null;
  const rel = anchor.getAttribute('rel');
  if (rel && rel.split(/\s+/).includes('external')) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;

  if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash)
    return null;

  if (isNonAppPath(url.pathname)) return null;

  return url;
}

export function isNonAppPath(pathname: string): boolean {
  return (
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/api') ||
    pathname === '/health' ||
    /\.[a-z0-9]+$/i.test(pathname)
  );
}

export interface RouterProviderProps {
  children: ReactNode;
  initialLocation?: RouterLocation;
}

export function RouterProvider({ children, initialLocation }: RouterProviderProps) {
  const [location, setLocation] = useState<RouterLocation>(() => initialLocation ?? readLocation());

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (typeof window === 'undefined') return;
    const url = new URL(to, window.location.href);
    const next: RouterLocation = { pathname: url.pathname, search: url.search };
    if (options?.replace) {
      window.history.replaceState(null, '', to);
    } else {
      window.history.pushState(null, '', to);
    }
    setLocation(next);
    if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  }, []);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor || !anchor.getAttribute('href')) return;
      const url = interceptableUrl(event, anchor);
      if (!url) return;
      event.preventDefault();
      navigateRef.current(url.pathname + url.search);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const value = useMemo<RouterContextValue>(() => ({ location, navigate }), [location, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouterContext(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error('useLocation/useNavigate must be used within <RouterProvider>.');
  }
  return ctx;
}

export function useLocation(): RouterLocation {
  return useRouterContext().location;
}

export function useNavigate(): RouterContextValue['navigate'] {
  return useRouterContext().navigate;
}
